<template>
  <div class="weekly-schedule-timeline-card">
    <div class="card-inner-wrapper p-4 sm:p-5">
      <!-- Header / Controls Bar -->
      <div class="schedule-header flex flex-col md:flex-row md:items-center md:justify-between gap-3 pb-3 border-b">
        <!-- Title & Date Range -->
        <div class="flex items-center">
          <div class="header-icon-box mr-3 flex items-center justify-center rounded-lg">
            <feather-icon icon="CalendarIcon" svgClasses="h-5 w-5 text-primary" />
          </div>
          <div>
            <div class="flex items-center flex-wrap gap-2">
              <h4 class="font-bold text-base schedule-title leading-tight">Weekly Schedule</h4>
              <span class="week-range-badge text-xs px-2.5 py-0.5 rounded-full font-semibold">
                {{ currentWeekRangeLabel }}
              </span>
            </div>
            <p class="schedule-subtitle text-xs mt-0.5">
              Monday to Friday overview of company tasks & orders
            </p>
          </div>
        </div>

        <!-- Right Controls: Scope Toggle + Week Navigation & Open Full Button -->
        <div class="flex items-center flex-wrap gap-2">
          <!-- Scope Toggle -->
          <div class="scope-toggle-group flex rounded-lg p-0.5 border">
            <button
              type="button"
              class="scope-btn text-xs px-3 py-1 rounded-md font-semibold"
              :class="{ 'is-active': scope === 'all' }"
              @click="setScope('all')"
            >
              All Tasks
            </button>
            <button
              type="button"
              class="scope-btn text-xs px-3 py-1 rounded-md font-semibold"
              :class="{ 'is-active': scope === 'user' }"
              @click="setScope('user')"
            >
              My Tasks
            </button>
          </div>

          <!-- Week Navigation -->
          <div class="week-nav-container flex items-center rounded-lg border overflow-hidden">
            <button
              type="button"
              class="nav-icon-btn p-1.5 flex items-center justify-center"
              @click="changeWeek(-1)"
              title="Previous Week"
            >
              <feather-icon icon="ChevronLeftIcon" svgClasses="h-4 w-4" />
            </button>
            <button
              type="button"
              class="nav-today-btn text-xs px-2.5 py-1 font-medium"
              @click="goToCurrentWeek"
              title="Jump to current week"
            >
              This Week
            </button>
            <button
              type="button"
              class="nav-icon-btn p-1.5 flex items-center justify-center"
              @click="changeWeek(1)"
              title="Next Week"
            >
              <feather-icon icon="ChevronRightIcon" svgClasses="h-4 w-4" />
            </button>
          </div>

          <!-- Open Full Schedule Link Button -->
          <vs-button
            size="small"
            type="border"
            color="primary"
            icon-pack="feather"
            icon="icon-external-link"
            icon-after
            class="open-full-btn"
            @click="openFullSchedule"
          >
            Open Full Schedule
          </vs-button>
        </div>
      </div>

      <!-- Task Types Filter Pills Bar -->
      <div class="task-type-filters-bar">
        <button
          type="button"
          class="filter-pill"
          :class="{ 'is-active': selectedType === 'ALL' }"
          @click="selectType('ALL')"
        >
          All Tasks ({{ totalTasksCount }})
        </button>

        <button
          v-for="(color, type) in eventTypes"
          :key="type"
          type="button"
          class="filter-pill"
          :class="{ 'is-active': selectedType === type }"
          @click="selectType(type)"
        >
          <span class="type-dot" :style="{ backgroundColor: color }"></span>
          <span class="type-name">{{ type }}</span>
          <span v-if="getTypeCount(type) > 0" class="type-count">({{ getTypeCount(type) }})</span>
        </button>
      </div>

      <!-- Skeleton Loading Placeholders (5 Columns) -->
      <div v-if="isLoading" class="days-timeline-grid skeleton-grid">
        <div v-for="n in 5" :key="n" class="skeleton-day-column">
          <div class="skeleton-header">
            <div>
              <div class="skeleton-shimmer skeleton-bar w-12 mb-1"></div>
              <div class="skeleton-shimmer skeleton-bar w-16"></div>
            </div>
            <div class="skeleton-shimmer skeleton-badge"></div>
          </div>
          <div class="skeleton-items-list">
            <div class="skeleton-badge-item">
              <div class="skeleton-shimmer skeleton-pill-bar"></div>
            </div>
            <div class="skeleton-badge-item">
              <div class="skeleton-shimmer skeleton-pill-bar w-3/4"></div>
            </div>
            <div class="skeleton-badge-item" v-if="n % 2 === 0">
              <div class="skeleton-shimmer skeleton-pill-bar w-1/2"></div>
            </div>
          </div>
        </div>
      </div>

      <!-- 5 Days Monday to Friday Columns Grid -->
      <div v-else class="days-timeline-grid" id="weekly-schedule-timeline-grid">
        <div
          v-for="day in weekDays"
          :key="day.dateStr"
          class="day-timeline-column"
          :class="{
            'is-today': day.isToday,
            'is-past': day.isPast,
            'is-future': day.isFuture
          }"
        >
          <!-- Day Column Header -->
          <div class="day-column-header">
            <div class="day-meta-group">
              <div class="day-name-wrapper">
                <span class="day-name-label" :class="{ 'is-today-text': day.isToday }">{{ day.dayName }}</span>
                <span v-if="day.isToday" class="today-indicator-pill">Today</span>
              </div>
              <span class="day-date-label">{{ day.dateDisplay }}</span>
            </div>
            <span
              class="day-tasks-badge"
              :class="day.totalDayTasksCount > 0 ? 'badge-has-tasks' : 'badge-empty'"
            >
              {{ day.totalDayTasksCount }}
            </span>
          </div>

          <!-- Grouped Events List in Day Column -->
          <div class="day-tasks-list">
            <div
              v-for="(group, gIdx) in day.groupedEvents"
              :key="group.type + '_' + gIdx"
              class="event-badge-item cursor-pointer"
              :style="{
                backgroundColor: getEventColor(group.type) + '18',
                border: `1px solid ${getEventColor(group.type)}40`
              }"
              @click="onClickEventBadge(group, day)"
              @mouseenter="onBadgeMouseEnter($event, group, day)"
              @mouseleave="scheduleHideLeaveTooltip"
            >
              <!-- Multi-item Group Badge -->
              <div v-if="group.isGroup" class="group-task-content flex items-center justify-between">
                <div class="flex items-center overflow-hidden flex-1 mr-2">
                  <span
                    class="event-count-badge"
                    :style="{ backgroundColor: getEventColor(group.type) }"
                  >
                    {{ group.count }}
                  </span>
                  <span
                    class="event-group-title font-semibold truncate"
                    :style="{ color: getEventColor(group.type) }"
                  >
                    {{ group.type }}
                  </span>
                </div>
                <feather-icon
                  icon="ChevronRightIcon"
                  svgClasses="h-4 w-4 opacity-75 flex-shrink-0"
                  :style="{ color: getEventColor(group.type) }"
                />
              </div>

              <!-- Single Task Badge -->
              <div v-else class="single-task-content">
                <div class="single-task-header" :style="{ color: getEventColor(group.type) }">
                  <span class="single-task-type truncate">{{ group.type }}</span>
                  <span v-if="group.items[0].id" class="single-task-id">#{{ group.items[0].id }}</span>
                </div>
                <div class="single-task-title">
                  {{ group.items[0].title }}
                </div>
              </div>
            </div>

            <!-- Empty State for Day -->
            <div v-if="day.groupedEvents.length === 0" class="empty-day-state">
              <feather-icon icon="CheckCircleIcon" svgClasses="h-5 w-5 mx-auto mb-1 opacity-30" />
              <span class="text-3xs block">No tasks</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Staff on Leave hover tooltip (matches MySheduleMain.vue), moved to the body so it stays fixed to the screen -->
    <div
      ref="leaveTooltip"
      v-show="leaveTooltip"
      class="leave-tooltip"
      :style="leaveTooltip ? { top: `${leaveTooltip.top}px`, left: `${leaveTooltip.left}px` } : {}"
      @mouseenter="cancelHideLeaveTooltip"
      @mouseleave="scheduleHideLeaveTooltip"
      @click="onClickLeaveTooltip"
    >
      <template v-if="leaveTooltip">
        <div class="leave-tooltip-header" :style="{ borderLeft: `4px solid ${getEventColor('Staff Leave')}` }">
          <strong>Staff on Leave</strong>
        </div>
        <div class="leave-tooltip-date">{{ leaveTooltip.day.dateStr }}</div>
        <ul class="leave-tooltip-list">
          <li v-for="(staff, index) in leaveTooltip.staff" :key="staff.id || index" class="leave-tooltip-staff">
            {{ staff.name }}
          </li>
        </ul>
      </template>
    </div>

    <!-- Grouped Tasks Modal (matches MySheduleMain.vue) -->
    <vs-popup
      class="grouped-schedule-events-popup"
      :title="selectedGroup ? `${selectedGroup.type} (${selectedGroup.items.length} Tasks) — ${selectedGroup.dayName}, ${selectedGroup.dateDisplay}` : 'Grouped Tasks'"
      :active.sync="isGroupedTasksModalOpen"
      @close="isGroupedTasksModalOpen = false"
    >
      <div v-if="selectedGroup" class="grouped-tasks-container p-2">
        <p class="text-xs text-muted mb-4 font-medium">
          Select any task to open details:
        </p>
        <div class="grouped-task-list max-h-80 overflow-y-auto pr-1">
          <div
            v-for="(item, index) in selectedGroup.items"
            :key="item.id || index"
            class="grouped-task-card flex items-center justify-between p-3 mb-3 rounded-lg border cursor-pointer hover:shadow-md transition-all"
            :style="{ borderLeft: `5px solid ${getEventColor(item.type)}` }"
            @click="onClickGroupedItem(item)"
          >
            <div class="flex flex-col pr-3 overflow-hidden flex-1">
              <span class="font-semibold text-xs text-dark leading-snug">{{ item.title }}</span>
            </div>
            <vs-button
              size="small"
              type="flat"
              icon-pack="feather"
              icon="icon-external-link"
              :color="getEventColor(item.type)"
              class="flex-shrink-0 ml-2"
              @click.stop="onClickGroupedItem(item)"
            >
              Open
            </vs-button>
          </div>
        </div>
      </div>
    </vs-popup>

    <!-- Single Event Detail PopUp -->
    <ViewCompanyScheduleEventPopUp ref="viewCompanyScheduleEventPopUp" />
  </div>
</template>

<script>
import { RepositoryFactory } from '@/repository/RepositoryFactory'
const CompanyScheduleRepository = RepositoryFactory.get('company_schedule')
import ViewCompanyScheduleEventPopUp from '@/components/popups/company-schedule/ViewCompanyScheduleEventPopUp.vue'

export default {
  name: 'WeeklyScheduleTimelineCard',
  components: {
    ViewCompanyScheduleEventPopUp
  },
  data() {
    return {
      currentDate: new Date(),
      scope: 'all',
      selectedType: 'ALL',
      isLoading: false,
      scheduleRawData: [],
      isGroupedTasksModalOpen: false,
      selectedGroup: null,
      leaveTooltip: null,
      leaveTooltipHideTimeout: null,
      eventTypes: {
        'Raw Material Order': '#132440',
        'Scheduled Task': '#628141',
        'Other Order': '#F25912',
        'Sales Order': '#5C3E94',
        'Work Order': '#A27B5C',
        'Staff Task': '#255F38',
        'Staff Leave': '#66005A',
        'Staff Birthday': '#A2678A',
        'Closed Holidays': '#A64942'
      }
    }
  },
  computed: {
    // Generates Monday through Friday array for the active week with grouped events and past/today/future flags
    weekDays() {
      const startOfWeek = this.getMonday(this.currentDate)
      const days = []
      const todayStr = this.formatDateSlash(new Date())

      const todayMidnight = new Date()
      todayMidnight.setHours(0, 0, 0, 0)

      for (let i = 0; i < 5; i++) {
        const d = new Date(startOfWeek)
        d.setDate(startOfWeek.getDate() + i)
        const dateFormatted = this.formatDateSlash(d) // DD/MM/YYYY
        const dayName = d.toLocaleDateString('en-US', { weekday: 'short' })
        const dateDisplay = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

        const checkMidnight = new Date(d)
        checkMidnight.setHours(0, 0, 0, 0)

        const isPast = checkMidnight.getTime() < todayMidnight.getTime()
        const isToday = checkMidnight.getTime() === todayMidnight.getTime()
        const isFuture = checkMidnight.getTime() > todayMidnight.getTime()

        // 1. Filter raw events for this date
        const dayTasks = this.scheduleRawData.filter(item => {
          if (!item.start_date) return false
          if (item.start_date === dateFormatted) return true

          // Check if date falls in range start_date -> end_date
          if (item.end_date) {
            const startParts = item.start_date.split('/').map(Number)
            const endParts = item.end_date.split('/').map(Number)
            const currentParts = dateFormatted.split('/').map(Number)

            const startDate = new Date(startParts[2], startParts[1] - 1, startParts[0])
            const endDate = new Date(endParts[2], endParts[1] - 1, endParts[0])
            const curDate = new Date(currentParts[2], currentParts[1] - 1, currentParts[0])

            return curDate >= startDate && curDate <= endDate
          }
          return false
        })

        // 2. Filter by selected task type pill if active
        const filteredTasks = this.selectedType === 'ALL'
          ? dayTasks
          : dayTasks.filter(t => t.type === this.selectedType)

        // 3. Group by task type (same algorithm as MySheduleMain.vue)
        const groupsByType = {}
        filteredTasks.forEach(task => {
          const type = task.type || 'Other'
          if (!groupsByType[type]) {
            groupsByType[type] = {
              type,
              items: [],
              dayName,
              dateDisplay,
              dateFormatted
            }
          }
          groupsByType[type].items.push({
            id: task.id,
            title: task.title || 'N/A',
            type: task.type,
            data: task.data,
            start_date: task.start_date,
            end_date: task.end_date
          })
        })

        const groupedEvents = Object.values(groupsByType).map(group => ({
          type: group.type,
          count: group.items.length,
          isGroup: group.items.length > 1,
          items: group.items,
          dayName,
          dateDisplay,
          dateFormatted
        }))

        days.push({
          date: d,
          dateStr: dateFormatted,
          dayName,
          dateDisplay,
          isToday,
          isPast,
          isFuture,
          totalDayTasksCount: filteredTasks.length,
          groupedEvents
        })
      }
      return days
    },
    totalTasksCount() {
      return this.scheduleRawData.length
    },
    currentWeekRangeLabel() {
      const mon = this.getMonday(this.currentDate)
      const fri = new Date(mon)
      fri.setDate(mon.getDate() + 4)
      return `${mon.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${fri.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    }
  },
  methods: {
    getMonday(d) {
      const date = new Date(d)
      const day = date.getDay()
      const diff = date.getDate() - day + (day === 0 ? -6 : 1)
      return new Date(date.setDate(diff))
    },
    formatDateSlash(date) {
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    },
    getEventColor(type) {
      return this.eventTypes[type] || '#7367F0'
    },
    getTypeCount(type) {
      return this.scheduleRawData.filter(t => t.type === type).length
    },
    selectType(type) {
      this.selectedType = this.selectedType === type ? 'ALL' : type
    },
    setScope(newScope) {
      this.scope = newScope
      this.fetchWeeklySchedule()
    },
    changeWeek(offset) {
      const newDate = new Date(this.currentDate)
      newDate.setDate(newDate.getDate() + offset * 7)
      this.currentDate = newDate
      this.fetchWeeklySchedule()
    },
    goToCurrentWeek() {
      this.currentDate = new Date()
      this.fetchWeeklySchedule()
    },
    openFullSchedule() {
      this.$router.push('/pages/my-schedule').catch(() => {})
    },
    onClickEventBadge(group, day) {
      if (group.isGroup) {
        this.selectedGroup = {
          type: group.type,
          items: group.items,
          dayName: day.dayName,
          dateDisplay: day.dateDisplay,
          dateFormatted: day.dateStr
        }
        this.isGroupedTasksModalOpen = true
      } else if (group.items.length === 1) {
        this.onClickGroupedItem(group.items[0])
      }
    },
    onClickGroupedItem(item) {
      const taskId = item.id || (item.data && item.data.id)
      if (this.$refs.viewCompanyScheduleEventPopUp) {
        this.$refs.viewCompanyScheduleEventPopUp.openPopup({
          ...item,
          id: taskId,
          type: item.type,
          title: item.title,
          data: item.data,
          backgroundColor: this.getEventColor(item.type)
        })
      }
    },
    // Only Staff Leave badges have a hover tooltip, listing everyone on leave that day
    onBadgeMouseEnter(event, group, day) {
      if (group.type !== 'Staff Leave') return

      const staff = group.items.reduce((list, item) => list.concat((item.data && item.data.staff) || []), [])
      if (staff.length === 0) return

      this.cancelHideLeaveTooltip()
      const rect = event.currentTarget.getBoundingClientRect()
      this.leaveTooltip = {
        group,
        day,
        staff,
        top: rect.top,
        left: rect.right + 6
      }

      this.$nextTick(() => this.positionLeaveTooltip(rect))
    },
    // Right of the badge when it fits, otherwise left of it, kept inside the window
    positionLeaveTooltip(rect) {
      const tooltip = this.$refs.leaveTooltip
      if (!tooltip || !this.leaveTooltip) return

      const width = tooltip.offsetWidth
      const height = tooltip.offsetHeight
      let left = rect.right + 6
      if (left + width > window.innerWidth - 12) {
        left = Math.max(12, rect.left - width - 6)
      }
      const top = Math.max(12, Math.min(rect.top, window.innerHeight - height - 12))

      this.leaveTooltip.left = left
      this.leaveTooltip.top = top
    },
    scheduleHideLeaveTooltip() {
      this.cancelHideLeaveTooltip()
      this.leaveTooltipHideTimeout = setTimeout(() => {
        this.leaveTooltip = null
      }, 200)
    },
    cancelHideLeaveTooltip() {
      if (this.leaveTooltipHideTimeout) {
        clearTimeout(this.leaveTooltipHideTimeout)
        this.leaveTooltipHideTimeout = null
      }
    },
    onClickLeaveTooltip() {
      const { group, day } = this.leaveTooltip
      this.leaveTooltip = null
      this.onClickEventBadge(group, day)
    },
    async fetchWeeklySchedule() {
      const mon = this.getMonday(this.currentDate)
      const fri = new Date(mon)
      fri.setDate(mon.getDate() + 4)

      const from = this.formatDateSlash(mon)
      const to = this.formatDateSlash(fri)
      const allTypesStr = Object.keys(this.eventTypes).join(',')

      this.isLoading = true
      try {
        const { data } = (await CompanyScheduleRepository.getListOfCompanySchedule(
          from,
          to,
          allTypesStr,
          this.scope
        )).data

        this.scheduleRawData = data || []
      } catch (error) {
        console.error('Failed to load weekly schedule timeline:', error)
      } finally {
        this.isLoading = false
      }
    }
  },
  mounted() {
    document.body.appendChild(this.$refs.leaveTooltip)
    this.fetchWeeklySchedule()
  },
  beforeDestroy() {
    this.cancelHideLeaveTooltip()
    const tooltip = this.$refs.leaveTooltip
    if (tooltip && tooltip.parentNode) {
      tooltip.parentNode.removeChild(tooltip)
    }
  }
}
</script>

<style lang="scss" scoped>
.weekly-schedule-timeline-card {
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  box-shadow: 0 4px 18px 0 rgba(0, 0, 0, 0.04);
  transition: all 0.25s ease;

  &.edit-mode-active {
    animation: gentle-pulse 2s infinite ease-in-out;
  }

  .header-icon-box {
    width: 40px;
    height: 40px;
    background: rgba(var(--vs-primary), 0.1);
  }

  .schedule-title {
    color: #2c2c2c;
  }

  .schedule-subtitle {
    color: #727b87;
  }

  .week-range-badge {
    background: rgba(var(--vs-primary), 0.1);
    color: rgba(var(--vs-primary), 1);
  }
}

/* Scope Toggle Group */
.scope-toggle-group {
  background: #f8f9fa;
  border-color: rgba(0, 0, 0, 0.08);

  .scope-btn {
    border: none;
    background: transparent;
    color: #636b75;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
      color: #2c2c2c;
    }

    &.is-active {
      background: #ffffff;
      color: rgba(var(--vs-primary), 1);
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
      font-weight: 600;
    }
  }
}

/* Week Nav Container */
.week-nav-container {
  background: #f8f9fa;
  border-color: rgba(0, 0, 0, 0.08);

  .nav-icon-btn {
    color: #5e5873;
    border: none;
    background: transparent;
    cursor: pointer;
    transition: background-color 0.15s;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
      color: rgba(var(--vs-primary), 1);
    }
  }

  .nav-today-btn {
    border: none;
    background: transparent;
    color: #5e5873;
    cursor: pointer;
    border-left: 1px solid rgba(0, 0, 0, 0.08);
    border-right: 1px solid rgba(0, 0, 0, 0.08);
    transition: background-color 0.15s;

    &:hover {
      background: rgba(0, 0, 0, 0.05);
      color: rgba(var(--vs-primary), 1);
    }
  }
}

/* Filter Pills Bar */
.task-type-filters-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 10px 0;
  margin-bottom: 6px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  gap: 8px;

  .filter-pill {
    display: inline-flex;
    align-items: center;
    background: #f4f5f7;
    border: 1px solid rgba(0, 0, 0, 0.06);
    color: #5e6670;
    padding: 5px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;
    margin: 0;
    transition: all 0.2s ease;

    &:hover {
      background: #eaedf1;
      color: #2c2c2c;
    }

    &.is-active {
      background: rgba(var(--vs-primary), 0.12);
      border-color: rgba(var(--vs-primary), 0.4);
      color: rgba(var(--vs-primary), 1);
      font-weight: 600;
    }

    .type-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      margin-right: 6px;
      flex-shrink: 0;
    }

    .type-count {
      font-size: 10px;
      opacity: 0.8;
      margin-left: 4px;
    }
  }
}

/* 5 Days Timeline Grid - Strict CSS Grid with 14px Gaps */
.days-timeline-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
  margin-top: 8px;
  width: 100%;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
}

/* Day Column Card */
.day-timeline-column {
  background: #f8f9fb;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  padding: 14px;
  min-height: 220px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  /* Future days */
  &.is-future {
    background: #f8f9fb;
    border-color: rgba(0, 0, 0, 0.06);
  }

  /* Past days: subtly softened */
  &.is-past {
    background: #fcfcfd;
    opacity: 0.82;

    &:hover {
      opacity: 1;
    }
  }

  /* Today: Elevated with Top Gradient Accent Bar & Focus Border */
  &.is-today {
    background: #ffffff;
    border: 1.5px solid rgba(var(--vs-primary), 0.55);
    border-top: 4px solid rgba(var(--vs-primary), 1);
    box-shadow: 0 4px 18px rgba(var(--vs-primary), 0.12);
    transform: translateY(-2px);

    .day-name-label {
      color: rgba(var(--vs-primary), 1);
    }
  }
}

/* Day Column Header */
.day-column-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: 10px;
  margin-bottom: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.day-meta-group {
  display: flex;
  flex-direction: column;
}

.day-name-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 3px;
}

.day-name-label {
  font-size: 13px;
  font-weight: 700;
  color: #2c2c2c;
  line-height: 1.2;
}

.today-indicator-pill {
  background: rgba(var(--vs-primary), 0.15);
  color: rgba(var(--vs-primary), 1);
  font-size: 10px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 6px;
  line-height: 1.2;
  letter-spacing: 0.2px;
}

.day-date-label {
  font-size: 11.5px;
  color: #727b87;
  font-weight: 500;
  line-height: 1.2;
}

.day-tasks-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 12px;
  line-height: 1.2;
  flex-shrink: 0;

  &.badge-has-tasks {
    background: rgba(var(--vs-primary), 0.12);
    color: rgba(var(--vs-primary), 1);
  }

  &.badge-empty {
    background: rgba(0, 0, 0, 0.05);
    color: #9aa2ba;
  }
}

/* Day Tasks List & Events */
.day-tasks-list {
  flex: 1;
  overflow-y: auto;
  max-height: 240px;
  padding-right: 2px;
}

.event-badge-item {
  padding: 10px 12px;
  margin-bottom: 10px;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 3px 10px rgba(0, 0, 0, 0.07);
  }
}

.event-count-badge {
  color: #ffffff !important;
  font-size: 11px;
  font-weight: 700;
  min-width: 22px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 8px;
  line-height: 1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.event-group-title {
  font-size: 12px;
  letter-spacing: 0.1px;
}

/* Single Task Details Layout */
.single-task-content {
  display: flex;
  flex-direction: column;
}

.single-task-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  font-weight: 700;
  margin-bottom: 4px;
}

.single-task-id {
  font-size: 10.5px;
  font-weight: 600;
  opacity: 0.8;
  margin-left: 6px;
  flex-shrink: 0;
}

.single-task-title {
  font-size: 11.5px;
  font-weight: 500;
  line-height: 1.35;
  color: #2c2c2c;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.empty-day-state {
  padding: 30px 0;
  text-align: center;
  color: #9aa2ba;
}

/* Skeleton Loading Animation & Styles */
.skeleton-day-column {
  background: #f8f9fb;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  padding: 14px;
  min-height: 220px;
}

.skeleton-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: 10px;
  margin-bottom: 12px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.skeleton-shimmer {
  background: linear-gradient(90deg, #eceff1 25%, #f7f9fa 50%, #eceff1 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite linear;
}

.skeleton-bar {
  height: 12px;
  border-radius: 4px;

  &.w-12 { width: 48px; }
  &.w-16 { width: 64px; }
  &.w-20 { width: 80px; }
  &.w-24 { width: 96px; }
  &.w-32 { width: 128px; }
  &.w-3\/4 { width: 75%; }
  &.w-1\/2 { width: 50%; }
}

.skeleton-badge {
  width: 24px;
  height: 18px;
  border-radius: 10px;
}

.skeleton-items-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.skeleton-badge-item {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.05);
  border-radius: 8px;
  padding: 12px 14px;
}

.skeleton-pill-bar {
  height: 14px;
  width: 80%;
  border-radius: 4px;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* Staff on Leave Tooltip */
.leave-tooltip {
  position: fixed;
  z-index: 99999;
  width: 280px;
  padding: 12px 14px;
  background: #ffffff;
  color: #333333;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.18);
  font-size: 13px;
  cursor: pointer;
  box-sizing: border-box;
}

.leave-tooltip-header {
  padding-left: 6px;
  margin-bottom: 2px;
  font-size: 13.5px;
  font-weight: 600;
  color: #1e293b;
}

.leave-tooltip-date {
  font-size: 11.5px;
  color: #64748b;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid #f1f5f9;
}

.leave-tooltip-list {
  margin: 0;
  padding: 0;
  list-style: none;
  max-height: 220px;
  overflow-y: auto;
}

.leave-tooltip-staff {
  margin: 2px 0;
  padding: 4px 6px;
  border-radius: 4px;
  line-height: 1.35;
  font-size: 12px;
  color: #334155;
  word-break: break-word;
  transition: background 0.15s ease;

  &:hover {
    background: #f1f5f9;
  }
}

/* Grouped Tasks Modal */
::v-deep .grouped-schedule-events-popup {
  .vs-popup {
    min-width: 480px;
    max-width: 620px;
    border-radius: 12px;
  }

  .grouped-task-list {
    max-height: 380px;
    overflow-y: auto;
    padding: 2px 2px;
  }

  .grouped-task-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 12px 14px;
    margin-bottom: 12px !important;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);

    &:last-child {
      margin-bottom: 0 !important;
    }

    &:hover {
      background: #f8fafc;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    .text-dark {
      color: #2c2c2c;
    }
  }
}

/* Dark Theme Support */
::v-deep .theme-dark,
.theme-dark {
  .weekly-schedule-timeline-card {
    background: #10163a;
    border-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.35);

    .schedule-title {
      color: #ebeefd;
    }

    .schedule-subtitle {
      color: #9aa2ba;
    }

    .week-range-badge {
      background: rgba(115, 103, 240, 0.2);
      color: #b8b0fa;
    }
  }

  .scope-toggle-group {
    background: #1e2640;
    border-color: rgba(255, 255, 255, 0.1);

    .scope-btn {
      color: #9aa2ba;

      &:hover {
        color: #ebeefd;
      }

      &.is-active {
        background: #28314e;
        color: #d1ccff;
      }
    }
  }

  .week-nav-container {
    background: #1e2640;
    border-color: rgba(255, 255, 255, 0.1);

    .nav-icon-btn,
    .nav-today-btn {
      color: #b8b0fa;

      &:hover {
        background: rgba(255, 255, 255, 0.08);
      }
    }
  }

  .task-type-filters-bar {
    border-color: rgba(255, 255, 255, 0.06);

    .filter-pill {
      background: #1e2640;
      border-color: rgba(255, 255, 255, 0.08);
      color: #9aa2ba;

      &:hover {
        background: #28314e;
        color: #ebeefd;
      }

      &.is-active {
        background: rgba(115, 103, 240, 0.25);
        border-color: rgba(115, 103, 240, 0.5);
        color: #d1ccff;
      }
    }
  }

  .day-timeline-column {
    background: #171e40;
    border-color: rgba(255, 255, 255, 0.06);

    &.is-future {
      background: #171e40;
    }

    &.is-past {
      background: #141a38;
      opacity: 0.8;
    }

    &.is-today {
      background: #1b234b;
      border-color: rgba(115, 103, 240, 0.7);
      border-top: 4px solid rgba(115, 103, 240, 1);
      box-shadow: 0 4px 20px rgba(115, 103, 240, 0.25);
    }

    .day-name-label {
      color: #ebeefd;
    }

    .day-date-label {
      color: #9aa2ba;
    }

    .badge-empty {
      background: rgba(255, 255, 255, 0.06);
      color: #9aa2ba;
    }
  }

  .skeleton-day-column {
    background: #171e40;
    border-color: rgba(255, 255, 255, 0.06);
  }

  .skeleton-shimmer {
    background: linear-gradient(90deg, #1e2640 25%, #252e4d 50%, #1e2640 75%);
    background-size: 200% 100%;
  }

  .skeleton-badge-item {
    background: #1e2640;
    border-color: rgba(255, 255, 255, 0.06);
  }

  .single-task-title {
    color: #ebeefd;
  }

  .leave-tooltip {
    background: #1e1e2d;
    color: #cbd5e1;
    border-color: #334155;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.6);
  }

  .leave-tooltip-header {
    color: #f1f5f9;
  }

  .leave-tooltip-date {
    color: #94a3b8;
    border-bottom-color: #334155;
  }

  .leave-tooltip-staff {
    color: #e2e8f0;

    &:hover {
      background: #2a2a3e;
    }
  }

  .grouped-schedule-events-popup {
    .vs-popup {
      background: #10163a;
    }

    .grouped-task-card {
      background: #1e2640;
      border-color: rgba(255, 255, 255, 0.08);

      &:hover {
        background: #252e4d;
      }

      .text-dark {
        color: #ebeefd;
      }
    }
  }
}
</style>
